import { useState } from 'react'

import type { FillCard } from '@/models/fillApplication'
import { useFillJump } from '../App/Customers/FillApplication/FillJumpProvider'
import { focusFillCardError } from '../App/Customers/FillApplication/focusFillField'
import type { EditedCard } from './confirmSteps'

/** Saves the cards' unsaved edits in page order before a step acts on the application; the first that fails comes into view. */
export function useSaveEdits(edited: EditedCard[]) {
  const [saving, setSaving] = useState(false)
  const { showCard } = useFillJump()

  async function saveEdits(): Promise<boolean> {
    setSaving(true)
    try {
      for (const card of edited) {
        if (!(await card.form.save())) {
          // Its dialog may be closed, so open it, then focus the refused field once the dialog has mounted it.
          showCard(card.card)
          focusWhenShown(card.card)
          return false
        }
      }
      return true
    } finally {
      setSaving(false)
    }
  }

  return { saveEdits, saving }
}

/** Waits a few frames for the card's dialog to mount, then focuses its first refused field. */
function focusWhenShown(card: FillCard, frames = 10) {
  if (document.querySelector(`[data-fill-card="${card}"]`) || frames === 0) {
    focusFillCardError(card)
    return
  }
  requestAnimationFrame(() => focusWhenShown(card, frames - 1))
}
